import { webMcpControllerTools } from '../api/generated/api/webMcp'
import { errorMessage } from '../api/apiError'
import { callLightscapeTool } from './webMcpCall'

/** The MCP result shape WebMCP agents expect back from a tool. */
type McpToolResult = { content: { type: 'text'; text: string }[]; isError?: boolean }

type WebMcpTool = {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  annotations?: Record<string, unknown>
  execute: (input: Record<string, unknown>) => Promise<McpToolResult>
}

/** Minimal, loose view of `navigator.modelContext` (WebMCP); the API is still moving. */
type ModelContext = {
  registerTool?: (
    tool: WebMcpTool,
    options?: { signal?: AbortSignal },
  ) => { unregister?: () => void } | void | Promise<unknown>
  unregisterTool?: (name: string) => void
  provideContext?: (context: { tools: WebMcpTool[] }) => void
  clearContext?: () => void
}

type ListedTool = Omit<WebMcpTool, 'execute'>
type ToolsAnswer = { instructions?: string; tools: ListedTool[] }

const modelContext = (): ModelContext | null =>
  ((navigator as Navigator & { modelContext?: ModelContext }).modelContext ?? null)

const textResult = (value: unknown, isError = false): McpToolResult => ({
  content: [{ type: 'text', text: JSON.stringify(value) }],
  ...(isError ? { isError: true } : {}),
})

const toWebMcpTool = (listed: ListedTool): WebMcpTool => ({
  name: listed.name,
  description: listed.description,
  inputSchema: listed.inputSchema,
  annotations: listed.annotations,
  execute: async (input) => {
    try {
      const outcome = await callLightscapeTool(listed.name, input ?? {})
      return outcome.ok ? textResult(outcome.result) : textResult(outcome.error, true)
    } catch (error) {
      return textResult({ error: errorMessage(error) }, true)
    }
  },
})

/**
 * Registers LightScape's canonical tools with the browser's WebMCP agent, forwarding every call
 * to the authenticated `/webmcp/call` gateway. A no-op without `navigator.modelContext`. Aborting
 * `signal` unregisters everything (sign-out, unmount).
 */
export const startWebMcp = async (signal: AbortSignal): Promise<void> => {
  const context = modelContext()
  if (!context) return
  const answer = (await webMcpControllerTools({ signal })) as ToolsAnswer
  if (signal.aborted) return
  const tools = answer.tools.map(toWebMcpTool)

  if (context.registerTool) {
    const registrations = tools.map((tool) => {
      try {
        return context.registerTool?.(tool, { signal })
      } catch (error) {
        console.warn(`[webmcp] could not register ${tool.name}`, error)
        return undefined
      }
    })
    signal.addEventListener('abort', () => {
      registrations.forEach((registration, index) => {
        const handle = registration as { unregister?: () => void } | undefined
        try {
          if (typeof handle?.unregister === 'function') handle.unregister()
          else context.unregisterTool?.(tools[index].name)
        } catch {
          /* already removed via the signal */
        }
      })
    })
  } else if (context.provideContext) {
    context.provideContext({ tools })
    signal.addEventListener('abort', () => {
      if (context.clearContext) context.clearContext()
      else context.provideContext?.({ tools: [] })
    })
  }
  console.info(`[webmcp] registered ${tools.length} LightScape tools`)
}
